{
  "app": {
    "cannotStart": "CosmosAI Studio cannot start"
  },
  "auth": {
    "signInFailed": "Sign-in failed",
    "signInAgain": "Sign in again",
    "signedOut": "Signed out",
    "signedOutText": "You have left CosmosAI Studio.",
    "signOut": "Sign out"
  },
  "health": {
    "nav": "Platform health",
    "title": "Platform health",
    "description": "The web backend, its database and the cosmos API.",
    "checking": "Checking…",
    "noAnswer": "The web backend did not answer. Is `pnpm dev` running?",
    "webApi": "Web backend {{version}}",
    "database": "Database cosmos_web",
    "cosmos": "cosmos API",
    "cosmosVersion": "cosmos API {{version}}",
    "status": {
      "ok": "ok",
      "down": "down"
    }
  },
  "ui": {
    "close": "Close",
    "copy": "Copy",
    "copied": "Copied",
    "error": {
      "title": "Something went wrong",
      "trace": "Trace ID",
      "copyTrace": "Copy the trace ID"
    },
    "status": {
      "draft": "Draft",
      "active": "Active",
      "archived": "Archived",
      "deployed": "Deployed",
      "deploying": "Deploying",
      "failed": "Failed"
    },
    "copyCommand": "Copy the command",
    "outcome": {
      "changes": "Changes",
      "problems": "Problems",
      "error": "Error",
      "warning": "Warning",
      "nothing": "Nothing changed.",
      "change": {
        "created": "created",
        "updated": "updated",
        "unchanged": "unchanged",
        "deleted": "deleted"
      }
    },
    "command": {
      "typeName": "Type {{name}} to confirm",
      "refused": "The platform refused it",
      "cancel": "Cancel",
      "close": "Done",
      "running": "Working…"
    }
  },
  "shell": {
    "skipToContent": "Skip to content",
    "mainNavigation": "Main navigation",
    "openNavigation": "Open navigation",
    "brandHome": "CosmosAI Studio home",
    "brandSub": "Studio",
    "search": "Search or jump to…",
    "searchLabel": "Search or jump to ({{keys}})",
    "shortcutsHelp": "Help and keyboard shortcuts",
    "accountMenu": "Account menu, {{name}}",
    "demoBanner": {
      "title": "Demo data.",
      "text": "This Studio talks to a mock of the cosmos API: the data is fake and resets when the mock restarts."
    },
    "prodBanner": {
      "title": "Production is read-only.",
      "text": "Changes reach production only through promotion."
    },
    "status": {
      "checking": "Checking the platform…",
      "ok": "All systems operational",
      "degraded": "Some services are down",
      "unreachable": "Platform unreachable"
    }
  },
  "nav": {
    "groups": {
      "home": "Home",
      "work": "Work",
      "build": "Build",
      "resources": "Resources",
      "deliver": "Deliver",
      "monitor": "Monitor",
      "admin": "Admin",
      "account": "Account"
    }
  },
  "palette": {
    "title": "Search and commands",
    "description": "Type to filter pages and actions, then press Enter.",
    "placeholder": "Search pages and actions",
    "empty": "No results for “{{query}}”.",
    "move": "to move",
    "open": "to open",
    "close": "to close",
    "groups": {
      "pages": "Pages",
      "actions": "Actions",
      "specs": "Specs"
    }
  },
  "commands": {
    "theme": "Switch theme",
    "language": "Switch language",
    "shortcuts": "Show keyboard shortcuts",
    "uiGallery": "Open the UI gallery (development)"
  },
  "shortcuts": {
    "title": "Keyboard shortcuts",
    "description": "Every screen also works with the keyboard alone.",
    "palette": "Search or jump to anything",
    "help": "Show keyboard shortcuts",
    "escape": "Close a menu, panel or dialog"
  },
  "theme": {
    "toDark": "Switch to dark theme",
    "toLight": "Switch to light theme"
  },
  "user": {
    "organization_one": "Organization",
    "organization_other": "Organizations",
    "language": "Language",
    "languages": {
      "en": "English",
      "fr": "Français"
    },
    "securitySettings": "Security settings"
  },
  "account": {
    "loading": "Loading…",
    "profile": {
      "nav": "Profile",
      "title": "Profile",
      "description": "Details that help your colleagues reach you. Only you can see and change them.",
      "identity": "Sign-in identity",
      "name": "Name",
      "email": "Email",
      "managed": "Managed by your sign-in provider.",
      "details": "Details",
      "jobTitle": "Job title",
      "phone": "Phone",
      "phoneHint": "International format, such as +33 1 23 45 67 89.",
      "address": "Postal address",
      "addressHint": "Leave every field empty to keep no address.",
      "line1": "Address line 1",
      "line2": "Address line 2",
      "postalCode": "Postal code",
      "city": "City",
      "country": "Country code",
      "countryHint": "Two letters, such as FR or DE.",
      "save": "Save",
      "saving": "Saving…",
      "saved": "Profile saved.",
      "invalidSummary": "Some fields need your attention.",
      "invalid": {
        "jobTitle": "Use at most 120 characters.",
        "phone": "Enter 6 to 25 digits, spaces, dots, dashes or brackets, with an optional leading +.",
        "address": {
          "line1": "Enter the first address line (at most 200 characters).",
          "line2": "Use at most 200 characters.",
          "postalCode": "Enter a postal code (at most 20 characters).",
          "city": "Enter a city (at most 100 characters).",
          "country": "Enter a two-letter country code, such as FR."
        }
      },
      "changedTitle": "This profile changed in another tab or on another device.",
      "changedText": "Your changes are not saved and still in the form. Reload to see the latest version, then apply your changes again.",
      "reload": "Reload the profile",
      "data": "Your data",
      "dataText": "Download or erase what CosmosAI Studio keeps about you: this profile and your preferences. Your sign-in account and your work on the platform are not affected.",
      "export": "Download my data",
      "exporting": "Preparing…",
      "erase": "Erase my data",
      "eraseTitle": "Erase your Studio data?",
      "eraseText": "Your profile and preferences are deleted for good, and you are signed out. Your sign-in account and your work on the platform stay as they are.",
      "eraseConfirm": "Type {{value}} to confirm",
      "eraseAction": "Erase and sign out",
      "erasing": "Erasing…",
      "cancel": "Cancel"
    },
    "preferences": {
      "nav": "Preferences",
      "title": "Preferences",
      "description": "How CosmosAI Studio looks for you, on every device you sign in from. Changes apply at once.",
      "theme": "Theme",
      "themes": {
        "system": "Same as the system",
        "light": "Light",
        "dark": "Dark"
      },
      "language": "Language",
      "density": "Density",
      "densities": {
        "comfortable": "Comfortable",
        "compact": "Compact"
      },
      "notSaved": "Your preferences could not be saved; they apply to this page only."
    }
  },
  "context": {
    "change": "Change the workspace or project (now: {{context}})",
    "none": "none",
    "choose": "Choose a workspace",
    "workspace": "Workspace",
    "project": "Project",
    "allProjects": "All projects",
    "team": "Team {{team}}",
    "loading": "Loading…",
    "failed": "The list could not be loaded.",
    "environment": "Environment",
    "unreachable": "The cosmos API did not answer",
    "noWorkspace": "No workspace yet",
    "noWorkspaceText": "A workspace holds the teams, projects, environments and specs of a group. Create one with the cosmos CLI, then reload this page."
  },
  "kinds": {
    "agent": {
      "one": "Agent",
      "other": "Agents"
    },
    "prompt": {
      "one": "Prompt",
      "other": "Prompts"
    },
    "orchestration": {
      "one": "Orchestration",
      "other": "Orchestrations"
    },
    "knowledge-base": {
      "one": "Knowledge base",
      "other": "Knowledge bases"
    },
    "retriever": {
      "one": "Retriever",
      "other": "Retrievers"
    },
    "skill": {
      "one": "Skill",
      "other": "Skills"
    },
    "eval-suite": {
      "one": "Eval suite",
      "other": "Eval suites"
    },
    "dataset": {
      "one": "Dataset",
      "other": "Datasets"
    },
    "model": {
      "one": "Model",
      "other": "Models"
    },
    "mcp-server": {
      "one": "MCP server",
      "other": "MCP servers"
    },
    "connection": {
      "one": "Connection",
      "other": "Connections"
    },
    "schedule": {
      "one": "Schedule",
      "other": "Schedules"
    },
    "workspace": {
      "one": "Workspace",
      "other": "Workspaces"
    },
    "team": {
      "one": "Team",
      "other": "Teams"
    },
    "project": {
      "one": "Project",
      "other": "Projects"
    },
    "environment": {
      "one": "Environment",
      "other": "Environments"
    },
    "unknown": {
      "one": "Spec",
      "other": "Specs"
    }
  },
  "home": {
    "nav": "Home",
    "description": "Workspace {{workspace}}: its specs, the versions each environment runs and what changed lately.",
    "loading": "Loading…",
    "empty": "No spec in this workspace yet",
    "emptyText": "Apply YAML files with the cosmos CLI; their first versions land as drafts in the environment you name.",
    "noChanges": "Nothing moved in any environment yet.",
    "recent": "Recent changes",
    "recentText": "The last 20 events across environments.",
    "tiles": {
      "specs": "Specs",
      "total": "Spec objects, by the state of their newest version",
      "kinds": "Specs per kind",
      "deployed": "Versions deployed"
    },
    "tiers": {
      "development": "development",
      "staging": "staging",
      "production": "production"
    },
    "columns": {
      "spec": "Spec",
      "action": "Action",
      "environment": "Environment",
      "versions": "Versions",
      "by": "By",
      "at": "When"
    },
    "actions": {
      "apply": "Applied",
      "promote": "Promoted",
      "rollback": "Rolled back",
      "discard": "Discarded",
      "browse": "Browse specs",
      "scopes": "Teams and projects",
      "applyYaml": "Apply YAML"
    },
    "cli": {
      "title": "Use the CLI",
      "text": "Point the cosmos CLI at this workspace for the current shell.",
      "copyPowerShell": "Copy for PowerShell",
      "copyShell": "Copy for bash or zsh"
    }
  },
  "specs": {
    "title": "Specs",
    "description": "Every versioned spec of the workspace, with the version each environment runs.",
    "loading": "Loading the specs…",
    "favorite": "Favorite: {{name}}",
    "filters": {
      "search": "Search by name or reference",
      "kind": "Kind",
      "allKinds": "All kinds",
      "project": "Project",
      "allProjects": "All projects",
      "state": "State",
      "allStates": "All states"
    },
    "columns": {
      "favorite": "Favorite",
      "kind": "Kind",
      "name": "Name",
      "project": "Project",
      "latest": "Latest version",
      "createdBy": "Created by",
      "updated": "Updated"
    },
    "noMatch": "No spec matches these filters",
    "noMatchText": "Change the search, or clear the filters.",
    "clearFilters": "Clear the filters",
    "noKind": "{{kind}}: none yet",
    "noKindText": "Apply a YAML file of this kind with the cosmos CLI; its first version lands as a draft."
  },
  "spec": {
    "loading": "Loading the spec…",
    "breadcrumb": "Breadcrumb",
    "unknownKind": "“{{kind}}” is not a kind of the registry.",
    "runsNowhere": "Runs in no environment",
    "tabsLabel": "Spec sections",
    "tabs": {
      "overview": "Overview",
      "versions": "Versions",
      "yaml": "YAML",
      "diff": "Diff"
    },
    "fields": {
      "kind": "Kind",
      "reference": "Reference",
      "project": "Project",
      "versions": "Versions",
      "latestPublished": "Latest published",
      "runningIn": "Running in",
      "digest": "Digest of the latest version",
      "created": "Latest version created"
    },
    "copyDigest": "Copy the digest",
    "cli": "Read it with the CLI",
    "columns": {
      "version": "Version",
      "state": "State",
      "digest": "Digest",
      "created": "Created",
      "published": "Published",
      "environments": "Environments",
      "actions": "Actions"
    },
    "viewYaml": "YAML of v{{version}}",
    "compareWith": "Compare v{{version}}",
    "version": "Version",
    "yamlLabel": "YAML of {{name}} v{{version}}",
    "copyYaml": "Copy the YAML",
    "download": "Download",
    "diff": {
      "from": "From",
      "to": "To",
      "changed": "Changed: {{fields}}.",
      "same": "The documents are the same, apart from the version.",
      "single": "Only one version",
      "singleText": "Apply a new version of this spec to compare it with this one.",
      "label": "Changes of {{name}} from v{{from}} to v{{to}}"
    },
    "actions": {
      "publish": {
        "button": "Publish v{{version}}",
        "title": "Publish {{ref}}?",
        "text": "A published version never changes again and can be promoted to the next environments.",
        "confirm": "Publish v{{version}}"
      },
      "deprecate": {
        "button": "Deprecate v{{version}}",
        "title": "Deprecate {{ref}}?",
        "text": "It keeps running where it runs, but is never promoted again.",
        "confirm": "Deprecate v{{version}}"
      },
      "discard": {
        "button": "Discard v{{version}}",
        "title": "Discard {{ref}}?",
        "text": "The draft is deleted for good, with the development pointer to it. The platform refuses when a deployed spec uses it.",
        "confirm": "Discard v{{version}}"
      }
    }
  },
  "scopes": {
    "nav": "Teams and projects",
    "title": "Teams and projects",
    "description": "Teams own projects; project-level specs live in a project. Workspaces are created with the CLI.",
    "loading": "Loading the teams and projects…",
    "refused": "The platform refused it",
    "close": "Done",
    "columns": {
      "name": "Name",
      "displayName": "Display name",
      "projects": "Projects",
      "team": "Team",
      "version": "Version"
    },
    "fields": {
      "name": "Name",
      "nameHint": "Lowercase letters, digits and dashes; it cannot change later.",
      "nameInvalid": "Use lowercase letters, digits and dashes, at most 63 characters, starting and ending with a letter or digit.",
      "displayName": "Display name (optional)",
      "team": "Owning team",
      "teamRequired": "Choose the team that owns the project."
    },
    "team": {
      "title": "Teams",
      "new": "New team",
      "text": "A team owns projects and the people who work on them.",
      "create": "Create the team",
      "none": "No team yet"
    },
    "project": {
      "title": "Projects",
      "new": "New project",
      "text": "A project holds agents, prompts, knowledge bases and the other project-level specs.",
      "create": "Create the project",
      "none": "No project yet",
      "specs": "Specs of project {{project}}"
    }
  },
  "environments": {
    "nav": "Environments & promotion",
    "title": "Environments & promotion",
    "description": "Each object moves from development to staging to production by promotion. Production never changes any other way.",
    "board": "Versions per environment",
    "loading": "Loading…",
    "empty": "Nothing is deployed yet",
    "emptyText": "Apply YAML to a development environment; its versions show here and move up by promotion.",
    "noMatch": "No object matches these filters",
    "noMatchText": "Change the search, the kind or the project.",
    "columns": {
      "object": "Object"
    },
    "filters": {
      "search": "Search by name or reference",
      "kind": "Kind",
      "project": "Project",
      "applied_one": "Only the {{count}} object just applied",
      "applied_other": "Only the {{count}} objects just applied"
    },
    "cell": {
      "none": "Not in {{env}}",
      "sameAs": "Same as {{env}}",
      "publishFirst": "Publish it to promote to {{env}}",
      "publishFirstFor": "Publish {{ref}} to promote it to {{env}}"
    },
    "promote": {
      "button": "Promote to {{env}}",
      "buttonFor": "Promote {{ref}} to {{env}}",
      "title": "Promote {{ref}} to {{env}}?",
      "text": "{{env}} will run this version. The objects it uses must already run in {{env}}, or be promoted first.",
      "confirm": "Promote to {{env}}",
      "reference": "Reference",
      "from": "From",
      "to": "To",
      "replaces": "Replaces",
      "nothing": "Nothing: {{env}} does not run it yet"
    },
    "promoteMany": {
      "button": "Promote {{count}}",
      "open": "Promote {{count}} from {{from}} to {{env}}",
      "title": "Promote from {{from}} to {{env}}?",
      "text": "{{env}} will run the versions you keep checked, all in one promotion: objects promoted together may use one another.",
      "list": "Versions to promote",
      "new": "New in {{env}}",
      "replaces": "Replaces v{{version}}",
      "confirm": "Promote {{count}} to {{env}}"
    },
    "rollback": {
      "button": "Roll back",
      "buttonFor": "Roll back {{ref}} in {{env}}",
      "title": "Roll back {{ref}} in {{env}}?",
      "text": "{{env}} runs v{{version}} today. It goes back to the version you choose; v{{version}} stays in the history.",
      "confirm": "Roll back to v{{version}}",
      "current": "Running now",
      "choose": "Version to run again"
    },
    "history": {
      "button": "History",
      "open": "History of {{env}}",
      "text": "What moved here, newest first. Each event opens the diff of the two versions.",
      "events": "Events",
      "to": "to",
      "empty": "Nothing moved in {{env}} yet.",
      "diff": "Diff v{{from}} → v{{to}}",
      "version": "Open v{{version}}"
    }
  },
  "apply": {
    "nav": "Apply",
    "command": "Apply YAML",
    "title": "Apply YAML",
    "description": "Paste or upload spec files, read the plan, then apply them to a development environment. Nothing is kept when you leave.",
    "target": {
      "title": "Where",
      "project": "Project",
      "noProject": "No project (workspace-level kinds only)",
      "environment": "Environment",
      "production": "Staging and production are not listed: versions reach them by promotion only.",
      "cliNote": "The CLI reads these files from your disk."
    },
    "files": {
      "title": "Files",
      "new": "New file",
      "upload": "Upload files",
      "drop": "Drop YAML files here, upload them, or start a new file.",
      "unnamed": "(no path yet)",
      "remove": "Remove {{path}}"
    },
    "editor": {
      "title": "Editor",
      "path": "Path",
      "label": "YAML of {{path}}",
      "none": "No file selected",
      "noneText": "Add a file on the left, then select it to edit it."
    },
    "limits": {
      "title": "What blocks the plan",
      "files": "A batch holds at most {{max}} files.",
      "noPath": "Every file needs a path.",
      "path": "{{path}} is longer than {{max}} characters.",
      "twice": "Two files share the path {{path}}.",
      "size": "{{path}} is larger than {{max}}."
    },
    "plan": {
      "title": "Plan",
      "text": "The platform checks every file and lists what apply would change. Apply is enabled only for the inputs of a clean plan.",
      "button": "Plan",
      "none": "Plan the files to see what would change.",
      "stale": "The files, project or environment changed since the last plan: plan again.",
      "blocked": "Errors block the apply. Fix them, then plan again.",
      "changes": "What apply would change",
      "problems": "Problems",
      "batch": "The batch"
    },
    "apply": {
      "button": "Apply to {{env}}",
      "done": "Applied to {{env}}.",
      "board": "Open in the board"
    },
    "leave": {
      "title": "Leave without applying?",
      "text": "The files of this page are not kept: drafts belong to the platform, later.",
      "stay": "Stay",
      "go": "Leave"
    }
  },
  "labs": {
    "stream": {
      "title": "Streaming diagnostic",
      "description": "Ticks from the web API over Server-Sent Events: first event, intervals, heartbeats and resume after a drop.",
      "seconds": "Seconds",
      "start": "Start",
      "stop": "Stop",
      "disconnect": "Force a disconnect",
      "statusLabel": "Status",
      "firstEvent": "First event",
      "events": "Events",
      "heartbeats": "Heartbeats",
      "reconnects": "Reconnects",
      "gaps": "Gaps over 1.5 s",
      "intervals": "Intervals (min – max)",
      "lastEventId": "Last event id",
      "arrivals": "Last arrivals (id · interval)",
      "ms": "{{value}} ms",
      "notAvailable": "This page does not exist",
      "status": {
        "idle": "idle",
        "connecting": "connecting",
        "streaming": "streaming",
        "reconnecting": "reconnecting",
        "done": "done",
        "failed": "failed"
      }
    }
  },
  "files": {
    "nav": "Files",
    "title": "Files",
    "description": "Files of your organization, kept on the preview only (proof of concept).",
    "zone": "Files of your organization",
    "loading": "Loading the files…",
    "retry": "Retry",
    "empty": {
      "title": "No files yet",
      "text": "Drop files here or use Upload files. PDF, Office, images, text; up to {{size}}."
    },
    "columns": {
      "name": "Name",
      "size": "Size",
      "uploaded": "Uploaded",
      "actions": "Actions",
      "type": "Type"
    },
    "download": "Download {{name}}",
    "downloadFailed": "{{name}} could not be downloaded",
    "upload": {
      "button": "Upload files",
      "list": "Uploads in progress",
      "progress": "Upload of {{name}}",
      "percent": "{{percent}} %",
      "cancel": "Cancel",
      "cancelNamed": "Cancel the upload of {{name}}",
      "dismiss": "Dismiss",
      "dismissNamed": "Dismiss {{name}}",
      "done": "{{name}} uploaded.",
      "typeRefused": "This type of file is not accepted: PDF, Office, images and text only.",
      "tooLarge": "Larger than {{size}}, the limit.",
      "refused": "The storage refused the upload (HTTP {{status}}).",
      "unreachable": "The storage could not be reached.",
      "typeMismatch": "The replacement must have the same type as the file."
    },
    "delete": {
      "named": "Delete {{name}}",
      "title": "Delete {{name}}?",
      "text": "Everyone in your organization loses access to it.",
      "cancel": "Cancel",
      "confirm": "Delete",
      "done": "{{name}} deleted."
    },
    "crumbs": {
      "label": "Folder path",
      "root": "Files"
    },
    "search": {
      "label": "Search in this folder"
    },
    "select": {
      "one": "Select {{name}}",
      "all": "Select all",
      "count_one": "{{count}} selected",
      "count_other": "{{count}} selected"
    },
    "folderRow": {
      "empty": "This folder is empty.",
      "noMatch": "Nothing matches “{{query}}” in this folder."
    },
    "actions": {
      "menu": "Actions for {{name}}",
      "preview": "Preview",
      "details": "Details",
      "download": "Download",
      "rename": "Rename",
      "move": "Move to",
      "replace": "Replace",
      "delete": "Delete",
      "deleteFolder": "Delete folder"
    },
    "newFolder": {
      "button": "New folder",
      "title": "New folder",
      "text": "In {{folder}}.",
      "label": "Folder name",
      "hint": "Up to 60 characters, without / or \\.",
      "invalid": "Use 1 to 60 characters, without / or \\; a folder is not named like a file id.",
      "tooDeep": "Folders are at most five levels deep.",
      "confirm": "Create",
      "done": "Folder {{name}} created."
    },
    "rename": {
      "title": "Rename {{name}}",
      "text": "The file keeps its folder and its type.",
      "label": "New name",
      "hint": "Keep the extension {{extension}}.",
      "invalid": "Enter a name with at least one letter or digit.",
      "typeMismatch": "Keep the extension: a file keeps its type.",
      "confirm": "Rename",
      "done": "Renamed to {{name}}."
    },
    "move": {
      "title": "Move {{name}}",
      "text": "The file keeps its name.",
      "label": "Folder",
      "confirm": "Move",
      "done": "{{name}} moved to {{folder}}."
    },
    "replace": {
      "input": "Replacement file"
    },
    "deleteMany": {
      "button": "Delete selected ({{count}})",
      "title_one": "Delete {{count}} file?",
      "title_other": "Delete {{count}} files?",
      "text": "Everyone in your organization loses access to them.",
      "tooMany": "Select 50 files at most.",
      "more": "and {{count}} more",
      "confirm": "Delete",
      "done_one": "{{count}} file deleted.",
      "done_other": "{{count}} files deleted.",
      "failed": "Not deleted:"
    },
    "deleteFolder": {
      "title": "Delete the folder {{name}}?",
      "text": "Everything inside it is deleted too: files and folders. Everyone in your organization loses access to them.",
      "confirm": "Delete folder",
      "done": "Folder {{name}} deleted."
    },
    "details": {
      "folder": "Folder",
      "size": "Size",
      "type": "Type",
      "uploaded": "Uploaded",
      "preview": "Preview",
      "previewOf": "Preview of {{name}}",
      "loading": "Loading the preview…",
      "none": "No preview for this type: download it.",
      "truncated": "Only the first 200 KB are shown. Download for the rest.",
      "failed": "The preview could not be loaded"
    }
  },
  "admin": {
    "users": {
      "nav": "Users",
      "title": "Users",
      "description": "People of your organization and their account type",
      "invite": "Invite people",
      "loading": "Loading the accounts…",
      "retry": "Retry",
      "you": "you",
      "empty": {
        "title": "No accounts yet",
        "text": "Invite the people of your organization."
      },
      "columns": {
        "person": "Person",
        "type": "Type",
        "status": "Status",
        "created": "Created",
        "actions": "Actions"
      },
      "status": {
        "invited": "Invited",
        "active": "Active",
        "disabled": "Disabled"
      },
      "actions": {
        "editName": "Edit name",
        "menu": "Actions for {{email}}",
        "changeType": "Change type",
        "disable": "Disable",
        "enable": "Enable",
        "resend": "Resend invitation",
        "remove": "Remove"
      },
      "resent": "Invitation sent again to {{email}}.",
      "resendFailed": "The invitation could not be sent again",
      "search": "Search people",
      "allTypes": "All types",
      "allStatuses": "All statuses",
      "count": "{{shown}} of {{total}} people",
      "page": "{{page}} / {{pages}}",
      "previousPage": "Previous page",
      "nextPage": "Next page",
      "noMatch": {
        "title": "No one matches",
        "text": "Change the search or the filters."
      }
    },
    "invite": {
      "title": "Invite people",
      "text": "They join your organization with the account type you choose.",
      "emails": "Email addresses",
      "placeholder": "one per line",
      "hint": "Each person gets an email to verify the address and set a password; the link is valid 24 hours.",
      "type": "Account type",
      "invalid": "Not an email address: {{lines}}",
      "tooMany": "At most 20 addresses at a time.",
      "duplicate": "Each address only once.",
      "empty": "Enter at least one address.",
      "cancel": "Cancel",
      "send": "Send invitations",
      "done": "Done",
      "results": "Invitations",
      "outcome": {
        "invited": "Invited",
        "exists": "Has an account elsewhere",
        "failed": "Failed, try again"
      }
    },
    "changeType": {
      "title": "Change the type of {{email}}",
      "text": "The person is signed out and gets the new type at the next sign-in.",
      "type": "Account type",
      "confirm": "Change type",
      "done": "{{email}} is now {{type}}."
    },
    "disable": {
      "title": "Disable {{email}}?",
      "text": "The person is signed out and cannot sign in until the account is enabled again.",
      "confirm": "Disable",
      "done": "{{email}} disabled."
    },
    "enable": {
      "title": "Enable {{email}}?",
      "text": "The person can sign in again with the same account type.",
      "confirm": "Enable",
      "done": "{{email}} enabled."
    },
    "remove": {
      "title": "Remove {{email}}?",
      "text": "The account is disabled and leaves the organization; the platform's data stays.",
      "confirm": "Remove",
      "done": "{{email}} removed."
    },
    "types": {
      "tenant-admin": "Super admin",
      "admin": "Admin",
      "user": "User"
    },
    "problems": {
      "adminNotConfigured": "Account administration is not configured",
      "accountTypeNotAllowed": "Your account type does not allow this",
      "organizationAmbiguous": "Your account must belong to exactly one organization",
      "ownAccount": "You cannot change your own account here",
      "lastSuperAdmin": "The organization needs a super admin",
      "notInvited": "This account is not waiting for an invitation",
      "identityProviderUnavailable": "The identity provider did not answer",
      "organizationNotFound": "Your organization was not found"
    },
    "editName": {
      "title": "Edit the name of {{email}}",
      "text": "The person stays signed in; the new name shows at the next sign-in.",
      "firstName": "First name",
      "lastName": "Last name",
      "confirm": "Save",
      "done": "Name of {{email}} saved."
    },
    "organization": {
      "nav": "Organization",
      "title": "Organization",
      "description": "Your organization as Keycloak knows it; only its name can change here.",
      "loading": "Loading the organization…",
      "name": "Name",
      "alias": "Alias",
      "domains": "Domains",
      "counts": "Members",
      "count": {
        "tenant-admin_one": "{{count}} super admin",
        "tenant-admin_other": "{{count}} super admins",
        "admin_one": "{{count}} admin",
        "admin_other": "{{count}} admins",
        "user_one": "{{count}} user",
        "user_other": "{{count}} users",
        "invited_one": "{{count}} invited",
        "invited_other": "{{count}} invited",
        "disabled_one": "{{count}} disabled",
        "disabled_other": "{{count}} disabled"
      },
      "rename": {
        "open": "Rename",
        "title": "Rename the organization",
        "text": "The alias stays: sign-in and the files keep using it.",
        "confirm": "Rename",
        "done": "The organization is now {{name}}."
      }
    }
  },
  "knowledge": {
    "tabs": {
      "documents": "Documents",
      "generations": "Generations"
    },
    "loading": "Loading the knowledge base…",
    "retryLoad": "Try again",
    "upload": {
      "button": "Upload documents",
      "drop": "Drop documents here",
      "hint": "Drop PDF, Word, text, Markdown, HTML or image files; each is ingested in the generations of this knowledge base. Status shown for {{environment}}.",
      "list": "Uploads",
      "progress": "Upload of {{name}}",
      "cancel": "Cancel",
      "cancelNamed": "Cancel the upload of {{name}}",
      "dismiss": "Dismiss",
      "dismissNamed": "Dismiss {{name}}",
      "queued": "Uploaded, ingestion queued",
      "unchanged": "Already in this knowledge base"
    },
    "empty": {
      "title": "No documents yet",
      "text": "Upload documents to build this knowledge base's index."
    },
    "count_one": "{{count}} document",
    "count_other": "{{count}} documents",
    "columns": {
      "name": "Name",
      "type": "Type",
      "size": "Size",
      "added": "Added",
      "status": "Status in {{environment}}"
    },
    "status": {
      "queued": "Queued",
      "running": "Running",
      "ready": "Ready",
      "failed": "Failed",
      "deleting": "Deleting",
      "absent": "Not in this environment"
    },
    "retried_one": "{{count}} ingestion queued again",
    "retried_other": "{{count}} ingestions queued again",
    "sheet": {
      "gone": "Document removed",
      "description": "The document and its ingestion in each generation.",
      "copySha": "Copy the SHA-256",
      "ingestions": "Ingestions",
      "servedNowhere": "served nowhere",
      "attempts": "Attempts",
      "pages": "Pages",
      "chunks": "Chunks",
      "tokens": "Tokens",
      "cost": "Cost",
      "error": "Error",
      "retry": "Retry",
      "delete": "Delete document",
      "deleteTitle": "Delete this document?",
      "deleteText": "{{name}} leaves every generation of this knowledge base. Answers already given keep their citations."
    },
    "generations": {
      "retryFailed": "Retry failed",
      "refresh": "Refresh URL sources",
      "refreshing": "URL sources are fetched again",
      "empty": "No generation yet",
      "emptyText": "A generation is built once this knowledge base runs in an environment.",
      "digest": "Digest",
      "status": "Status",
      "environments": "Served in",
      "waiting": "Waiting",
      "created": "Created",
      "liveSince": "Live since",
      "copyDigest": "Copy the digest",
      "statuses": {
        "building": "Building",
        "live": "Live",
        "retired": "Retired",
        "failed": "Failed"
      }
    },
    "problems": {
      "document_too-large": "The file is larger than 50 MiB, the most a knowledge base takes.",
      "document_unsupported-type": "This type of file is not read here: use PDF, Word, text, Markdown, HTML or an image.",
      "document_deleting": "This document is being deleted; upload it again once it is gone.",
      "document_not-found": "This document no longer exists.",
      "storage_not-ready": "The document store does not answer; try again shortly.",
      "kb_not-found": "This knowledge base does not exist."
    },
    "chunks_one": "{{count}} chunk",
    "chunks_other": "{{count}} chunks",
    "pages_one": "{{count}} page",
    "pages_other": "{{count}} pages"
  },
  "runs": {
    "nav": "Runs",
    "tab": "Runs",
    "title": "Runs",
    "description": "Runs of the agents of {{project}}, most recent first.",
    "loading": "Loading the runs…",
    "breadcrumb": "Breadcrumb",
    "openAll": "Open all runs of this agent",
    "empty": {
      "title": "No runs yet",
      "text": "Runs appear here once an agent of this project answers a question."
    },
    "filters": {
      "agent": "Agent",
      "allAgents": "All agents",
      "conversation": "Conversation {{id}}",
      "clear": "Clear the conversation filter"
    },
    "columns": {
      "run": "Run",
      "agent": "Agent",
      "environment": "Environment",
      "status": "Status",
      "question": "Question",
      "created": "Created",
      "duration": "Duration",
      "cost": "Cost"
    },
    "status": {
      "queued": "Queued",
      "running": "Running",
      "done": "Done",
      "failed": "Failed",
      "cancelled": "Cancelled"
    },
    "stages": {
      "rewriting": "Rewriting the question",
      "searching": "Searching",
      "generating": "Writing"
    },
    "cached": "From the cache",
    "cancel": "Cancel",
    "erase": "Erase",
    "eraseTitle": "Erase this run?",
    "eraseText": "The run, its answer and its cached copy are erased for good.",
    "erased": "Run erased",
    "created": "Created",
    "started": "Started",
    "finished": "Finished",
    "tokens": "Tokens",
    "answer": "Answer",
    "error": "Error",
    "warnings": "Warnings",
    "passages": "Passages",
    "cited": "Cited",
    "page": "page {{page}}",
    "score": "score {{score}}",
    "textGone": "Text no longer available",
    "citation": "Source {{number}}",
    "stagesTitle": "Stages",
    "reconnecting": "reconnecting",
    "streamLost": "Lost the live stream of this run",
    "streamRetry": "Follow again",
    "usage": "Model calls",
    "call": "Call",
    "tokensInOut": "Tokens in / out",
    "openChat": "Open the chat"
  },
  "chat": {
    "nav": "Chat",
    "command": "Ask an agent",
    "title": "Chat",
    "description": "Ask the rag agents of {{project}} deployed in this environment.",
    "agent": "Agent",
    "chooseAgent": "Choose an agent",
    "loadingAgents": "Loading the agents…",
    "notRunnable": "{{name}} ({{type}}: not available in chat yet)",
    "noAgents": {
      "title": "No agent runs here",
      "text": "No agent of this project is deployed in this environment.",
      "action": "Open the environments"
    },
    "pick": {
      "title": "Choose an agent",
      "text": "Pick a deployed rag agent to ask it a question."
    },
    "conversations": "Conversations",
    "conversation": "Conversation",
    "newConversation": "New conversation",
    "loadingConversations": "Loading the conversations…",
    "noConversations": "No conversation yet.",
    "untitled": "Untitled conversation",
    "eraseNamed": "Erase the conversation {{title}}",
    "eraseTitle": "Erase the conversation “{{title}}”?",
    "eraseText": "Its questions, answers and runs are erased for good.",
    "erase": "Erase the conversation",
    "erased": "Conversation erased",
    "empty": {
      "title": "Ask a question",
      "text": "{{agent}} answers from its knowledge base and cites its sources."
    },
    "turns": "Questions and answers",
    "you": "You:",
    "waiting": "Waiting for a worker",
    "stages": {
      "rewriting": "Rewriting the question",
      "searching": "Searching",
      "generating": "Writing"
    },
    "question": "Question",
    "placeholder": "Ask a question (Enter sends, Shift+Enter breaks a line)",
    "counter": "{{count}} / {{max}}",
    "send": "Send",
    "stop": "Stop",
    "stopped": "Stopped",
    "askAgain": "Ask again",
    "cached": "From the cache",
    "tokens": "{{tokens}} tokens",
    "runDetails": "Run details",
    "showSources": "Show the sources",
    "sources": "Sources",
    "otherPassages": "Other passages found",
    "noSources": "The sources of the selected answer appear here.",
    "noCitations": "The answer cites no passage.",
    "refusals": {
      "agent": {
        "not-deployed": "This agent does not run in {{environment}}.",
        "unsupported-type": "This agent's type does not answer in the chat yet."
      },
      "kb": {
        "not-ready": "Its knowledge base has no ready index in {{environment}} yet."
      },
      "conversation": {
        "mismatch": "This conversation belongs to another agent or environment."
      },
      "run": {
        "question-too-long": "The question is too long.",
        "input-invalid": "The question is not valid."
      },
      "queue": {
        "not-ready": "No worker answers runs right now."
      }
    }
  }
}
